import { useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import TriplePointTakeoverHeroCarousel from './TriplePointTakeoverHeroCarousel'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import { encodeSearchParams } from 'lib/search/searchUtils'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import config from 'constants/config'
import Search from 'home/components/Search/Search'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { triplePointCampaignId, triplePointsTakeoverId } from 'takeovers/societyTriplePointsTakeover/societyTriplePointsTakeoverConfig'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import HttpStatus from 'components/Common/HttpStatus'
import useGlobalSearchVerticalTabs from 'hooks/GlobalSearch/useGlobalSearchVerticalTabs'
import { arrayToMap, nonNullable } from 'lib/array/arrayUtils'

const hotelFilters: App.OfferListFilters = {
  offerTypes: ['hotel', 'bedbank_hotel', 'bundle_and_save'],
  limit: 20,
  campaigns: [triplePointCampaignId],
}

const tacticalHotelFilters: App.OfferListFilters = {
  offerTypes: ['tactical_ao_hotel'],
  limit: 20,
  campaigns: [triplePointCampaignId],
}

const hotelSearchQuery = encodeSearchParams({
  searchItem: ANYWHERE_SEARCH_ITEM,
  rooms: [config.search.defaultOccupants],
  filters: hotelFilters,
})

const tourFilters: App.OfferListFilters = {
  offerTypes: ['tour_v2'],
  limit: 20,
  campaigns: [triplePointCampaignId],
}

const tourSearchQuery = encodeSearchParams({
  searchItem: ANYWHERE_SEARCH_ITEM,
  rooms: [config.search.defaultOccupants],
  filters: {
    campaigns: [triplePointCampaignId],
  },
})

const cruiseFilters: App.OfferListFilters = {
  offerTypes: ['cruise'],
  limit: 20,
  campaigns: [triplePointCampaignId],
  offerSource: 'cruises',
}

const cruiseSearchQuery = encodeSearchParams({
  searchItem: ANYWHERE_SEARCH_ITEM,
  rooms: [config.search.defaultOccupants],
  filters: {
    campaigns: [triplePointCampaignId],
  },
})

const experienceFilters: App.OfferListFilters = {
  offerTypes: ['experience'],
  limit: 20,
  campaigns: [triplePointCampaignId],
}

const experienceSearchQuery = encodeSearchParams({
  searchItem: ANYWHERE_SEARCH_ITEM,
  filters: {
    campaigns: [triplePointCampaignId],
  },
})

function TriplePointTakeoverLandingPage() {
  const currentTakeover = useCurrentSiteTakeover()
  const tabs = useGlobalSearchVerticalTabs()

  const tabsWeWant = useMemo(() => {
    return arrayToMap(nonNullable([
      tabs.get('hotels'),
      tabs.get('tours'),
      tabs.get('cruises'),
      tabs.get('experiences'),
    ]), (tab) => tab.id)
  }, [tabs])

  if (currentTakeover?.id !== triplePointsTakeoverId) {
    // page is only meant for when the triple points campaign is active
    return <HttpStatus code={404}>
      <Error404PageWithOffers />
    </HttpStatus>
  }

  return <div>
    <TriplePointTakeoverHeroCarousel />
    <Search tabs={tabsWeWant} />
    <OfferCardCarouselSection
      title={<FormattedMessage
        id="ZVeEuwzrguh5EAfn9Mia"
        defaultMessage="<i>3x</i> points on limited-time <i>hotel</i> offers"
        description="Section title for triple points hotel offers carousel"
        values={{ i: (chunks) => <i>{chunks}</i> }}
      />}
      filters={hotelFilters}
      allLink={`/search?${hotelSearchQuery.toString()}`}
      hideWhenEmpty
      tileStyle="card-default"
      tileSize="medium"
    />
    <OfferCardCarouselSection
      title={<FormattedMessage
        id="sTboInJV0aLQiU2WTVNK"
        defaultMessage="<i>3x</i> points on <i>hotel</i> offers"
        description="Section title for triple points tactical always-on hotel offers carousel on the campaign landing page"
        values={{ i: (chunks) => <i>{chunks}</i> }}
      />}
      filters={tacticalHotelFilters}
      allLink={`/search?${hotelSearchQuery.toString()}`}
      hideWhenEmpty
      tileStyle="card-default"
      tileSize="medium"
    />
    <OfferCardCarouselSection
      title={<FormattedMessage
        id="U1IYCoA77eAP_gaHh7r6"
        defaultMessage="<i>3x</i> points on selected <i>tours</i>"
        description="Section title for triple points tour offers carousel"
        values={{ i: (chunks) => <i>{chunks}</i> }}
      />}
      filters={tourFilters}
      allLink={`/search/tours?${tourSearchQuery.toString()}`}
      hideWhenEmpty
      tileStyle="card-default"
      tileSize="medium"
    />
    <OfferCardCarouselSection
      title={<FormattedMessage
        id="dF-cv7ewmdX6OWlpwtoy"
        defaultMessage="<i>3x</i> points on selected <i>cruises</i>"
        description="Section title for triple points cruise offers carousel"
        values={{ i: (chunks) => <i>{chunks}</i> }}
      />}
      filters={cruiseFilters}
      allLink={`/search/cruises?${cruiseSearchQuery.toString()}`}
      hideWhenEmpty
      tileStyle="card-default"
      tileSize="medium"
    />
    <OfferCardCarouselSection
      title={<FormattedMessage
        id="l0AdXUjIyjx5aJqY6eAy"
        defaultMessage="<i>3x</i> points on selected <i>experiences</i>"
        description="Section title for triple points experience offers carousel on the campaign landing page"
        values={{ i: (chunks) => <i>{chunks}</i> }}
      />}
      filters={experienceFilters}
      allLink={`/search/experiences?${experienceSearchQuery.toString()}`}
      hideWhenEmpty
      tileStyle="card-default"
      tileSize="medium"
    />
  </div>
}

export default TriplePointTakeoverLandingPage
